import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NavLink } from "react-router";
import { useAuth } from "~auth/useAuth";
import {
  DashboardLayout,
  DashboardLayoutColumn,
  DashboardLayoutNavigationGrid,
  DashboardLayoutNavigationGridBody,
  DashboardLayoutNavigationGridHeader,
} from "~components/Dashboard/DashboardLayout";
import { AddPhotoButton } from "~components/Resources/PhotoDocumentation/PhotoUpload/AddPhotoButton.tsx";
import { ContentSkeleton } from "~components/shared/Loading/ContentSkeleton/ContentSkeleton";
import { PageLayout } from "~components/shared/PageLayout/PageLayout";
import { useIsDesktop } from "~components/shared/useIsDesktop/useIsDesktop";
import { useEnabledNavigationDefinitions } from "~components/useNavigationDefinitions.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import type { TranslationKey } from "~contexts/I18nContext/I18nContext.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import { DashboardDirectLinks } from "./DashboardDirectLinks";
import {
  DashboardNavigationGroupItemView,
  DashboardNavigationGroupView,
} from "./DashboardNavigationGroupView";
import { DashboardTaskCards } from "./DashboardTaskCards";
import { SlideableTimeWidget } from "./SlideableTimeWidget/SlideableTimeWidget";
import { VacationEntitlementWidget } from "./VacationEntitlementWidget/VacationEntitlementWidget";

export function Dashboard() {
  const { number: tenantNumber } = useCurrentTenant();
  const t = useTranslation();
  const { navigationDefinitions, isLoading } =
    useEnabledNavigationDefinitions();
  const { activeAccount } = useAuth();
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));

  const orderGroup = (index: number, titleTranslationKey: TranslationKey) =>
    /* On desktop screen sizes the controlling group should be at the most
     * outer (3rd) column to be a bit separated from the other groups.
     */
    isMedium && titleTranslationKey === "navigationGroup_controlling"
      ? 3
      : index * 2;
  return (
    <PageLayout noPadding>
      {isLoading ? (
        <ContentSkeleton withPadding />
      ) : (
        <DashboardLayout variant="default">
          <DashboardLayoutColumn>
            <DashboardLayoutNavigationGrid>
              <DashboardLayoutNavigationGridHeader>
                <h1>
                  {t.dashboardGreeting}, {activeAccount?.name}!
                </h1>
              </DashboardLayoutNavigationGridHeader>
              <DashboardLayoutNavigationGridBody>
                {!isDesktop && (
                  <>
                    <SlideableTimeWidget />
                    <Box sx={{ mt: 3 }}>
                      <DashboardTaskCards showEmptyInfo={false} />
                    </Box>
                  </>
                )}
                {navigationDefinitions.map(
                  ({ items, titleTranslationKey, IconComponent }, index) => (
                    <DashboardNavigationGroupView
                      title={t[titleTranslationKey]}
                      icon={IconComponent && <IconComponent />}
                      key={titleTranslationKey}
                      style={{ order: orderGroup(index, titleTranslationKey) }}
                    >
                      {items.map(
                        ({
                          route,
                          titleTranslationKey: itemTitleTranslationKey,
                        }) => (
                          <NavLink
                            key={route.path}
                            to={route.link({ tenantNumber })}
                          >
                            <DashboardNavigationGroupItemView>
                              {t[itemTitleTranslationKey]}
                            </DashboardNavigationGroupItemView>
                          </NavLink>
                        ),
                      )}
                    </DashboardNavigationGroupView>
                  ),
                )}
              </DashboardLayoutNavigationGridBody>
            </DashboardLayoutNavigationGrid>
          </DashboardLayoutColumn>
          {isDesktop && (
            <DashboardLayoutColumn>
              <Stack
                sx={{
                  gap: 3,
                }}
              >
                <VacationEntitlementWidget />
                <div>
                  <DashboardTaskCards showEmptyInfo={true} />
                </div>
              </Stack>
              <DashboardDirectLinks />
            </DashboardLayoutColumn>
          )}
        </DashboardLayout>
      )}
      <AddPhotoButton />
    </PageLayout>
  );
}
